@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.service.LoginService
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun Fahrerkalender() {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val currentUser = LoginService.getUser()
    val currentUserId = currentUser?.id.orEmpty().trim()
    val currentUserName = listOf(currentUser?.vorname.orEmpty(), currentUser?.nachname.orEmpty())
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .trim()

    var selectedDate by remember { mutableStateOf(kalenderTodayIsoDate()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var bookings by remember { mutableStateOf<List<DriverBookingCardModel>>(emptyList()) }
    val yardLiveVersion = LocalScreenRefreshVersion.current

    LaunchedEffect(currentUserId, yardLiveVersion) {
        if (currentUserId.isBlank()) {
            loading = false
            bookings = emptyList()
            return@LaunchedEffect
        }

        loading = true
        error = null
        runCatching {
            loadDriverBookingCards(currentUserId, currentUserName)
        }.onSuccess {
            bookings = it
        }.onFailure {
            bookings = emptyList()
            error = it.message ?: t("driver_calendar_load_failed")
        }
        loading = false
    }

    val dayBookings = bookings
        .filter { it.booking.driverOrderDateKey() == selectedDate }
        .sortedWith(
            compareBy<DriverBookingCardModel> { it.booking.driverOrderTimeKey() }
                .thenBy { it.currentTargetLabel }
                .thenBy { it.booking.lieferscheinnummer }
                .thenBy { it.booking.id }
        )
    val lieferungen = dayBookings.count { it.artCode != SLOT_BEREICH_WA }
    val ladungen = dayBookings.count { it.artCode == SLOT_BEREICH_WA }
    val offen = dayBookings.count { !it.isDriverFinalAuftrag() }
    val abgeschlossen = dayBookings.count { it.isDriverFinalAuftrag() }

    fun openMeineAuftraege(artCode: String = "") {
        DriverFlowState.selectedMeineAuftraegeDateKey.value = selectedDate
        DriverFlowState.selectedMeineAuftraegeArtCode.value = artCode
        DriverFlowState.selectedScreenKey.value = "DriverMeineAuftraege"
    }

    Style(DriverOrderStyles)
    Div({ classes(DriverOrderStyles.page) }) {
        H3({ classes(DriverOrderStyles.title) }) { Text(t("driver_calendar")) }
        val currentError = error
        when {
            loading -> Div({ classes(DriverOrderStyles.noticeCard) }) { Text(t("driver_loading_calendar")) }
            currentError != null -> Div({ classes(DriverOrderStyles.errorCard) }) { Text(currentError) }
            else -> Div({ classes(DriverOrderStyles.calendarCenter) }) {
                EngineMonthCalendar(
                    selectedDate = selectedDate,
                    onSelectDate = { selectedDate = it },
                    showFooter = false,
                    useWeekBorderLayout = false,
                    showSelectedDayBorder = true
                )

                Div({ classes(DriverOrderStyles.summaryGrid) }) {
                    DriverCalendarSummaryCard(
                        label = t("driver_deliveries"),
                        value = lieferungen.toString(),
                        onClick = { openMeineAuftraege(SLOT_BEREICH_WE) }
                    )
                    DriverCalendarSummaryCard(
                        label = t("driver_loads"),
                        value = ladungen.toString(),
                        onClick = { openMeineAuftraege(SLOT_BEREICH_WA) }
                    )
                    DriverCalendarSummaryCard(
                        label = t("driver_open"),
                        value = offen.toString(),
                        onClick = { openMeineAuftraege() }
                    )
                    DriverCalendarSummaryCard(t("driver_completed"), abgeschlossen.toString())
                }
            }
        }
    }
}

@Composable
private fun DriverCalendarSummaryCard(label: String, value: String, onClick: (() -> Unit)? = null) {
    Div({
        classes(DriverOrderStyles.summaryCard)
        if (onClick != null) {
            classes(DriverOrderStyles.summaryCardClickable)
            onClick { onClick() }
        }
    }) {
        Span({ classes(DriverOrderStyles.summaryLabel) }) { Text(label) }
        Span({ classes(DriverOrderStyles.summaryValue) }) { Text(value) }
    }
}
